import { get } from 'lodash-es'
import { renameKeys } from './utils'
import COMMON_FPTI_TAGS from '@/constants/fpti-tags/common'
import { EnrollCardEvents } from '@/constants/fpti-tags/enroll-card'

const { recordFptiErrorTags } = COMMON_FPTI_TAGS

const initFPTIData = {}
const FPTIData = {}

function initFPTI(serverData) {
  // Initial FPTI data generated by node-analytics module on render
  // in res.locals.sys.tracking.fpti.dataString, e.g. calc, rsta, comp,
  // pgtf, pgst, gacook, and others
  Object.assign(initFPTIData, window.fpti)

  // Route specific FPTI data generated by the controller
  Object.assign(FPTIData, serverData)
}

function getInstance() {
  return get(window, 'PAYPAL.analytics.instance')
}

function getAnalytics() {
  return window.PAYPAL && window.PAYPAL.analytics
}

const allTagsNulled = {
  page: null,
  context_id: null,
  fi_id_list: null,
  balance_shown: null,
  chosen_fi_id: null,
  card_id: null,
  product_name: null,
  experimentation_experience: null,
  experimentation_treatment: null,
  apl_status: null,
  contingency_resolved: null,
  fi_id_and_contingency_list: null,
  int_error_desc: null,
  link: null,
  contingency: null,
  issuer_auth: null,
  erpg: null,
  erfd: null,
  eccd: null,
  error_type: null,
  error_description: null,
  error_msg: null,
  flfr: null,
}

function logActivity(pageStub, tags = {}) {
  recordImpression(pageStub, tags, true)
}

// This will pass existing tags set by record impression found in windows.fpti object.
function logActivityWithLastImpression(stubName, tags = {}) {
  const pageStub = `${window.fpti?.page}${stubName ? ':' + stubName : ''}`
  recordImpression(pageStub, tags, true)
}

function getContextId(tags = {}) {
  // context_id will be passed through webview header and should take precedence over `flowInstanceId` value
  const precendentValueForContextId = tags?.context_id
  return precendentValueForContextId || tags?.flowInstanceId
}

function recordImpression(pageStub, tags = {}, logActivityInstead = false) {
  const instance = getInstance()
  if (instance) {
    let page = ''
    if (pageStub) {
      page = FPTIData.page.replace('@stub', pageStub)
    }
    const fptiEntryPoint = get(tags, 'fptiEntryPoint')
    const opts = {
      ...allTagsNulled, // if adding a new tag, add it in this object too
      page,
      calc: get(tags, 'calc'),
      context_id: getContextId(tags),
      flfr: fptiEntryPoint,
      entry_point: fptiEntryPoint,
      fi_id_list: get(tags, 'fiIdList'),
      balance_shown: get(tags, 'balanceShown'),
      chosen_fi_id: get(tags, 'chosenFiId'),
      chosen_address_id: get(tags, 'chosenAddressId'),
      card_id: get(tags, 'cardId'),
      product_name: get(tags, 'productName'),
      experimentation_experience: get(tags, 'experimentIds'),
      experimentation_treatment: get(tags, 'treatmentIds'),
      apl_status: get(tags, 'applicationStatus'),
      contingency_resolved: get(tags, 'isContingencyResolved'),
      fi_id_and_contingency_list: get(tags, 'fiIdAndContingencyList'),
      message: get(tags, 'message'),
      int_error_desc: get(tags, 'intErrorDescription'),
      erpg: get(tags, 'errorPage'),
      erfd: get(tags, 'errorField'),
      eccd: get(tags, 'errorCode'),
      user_guid: get(tags, 'user_guid'),
      session_guid: get(tags, 'session_guid'),
      error_type: get(tags, 'errorType'),
      // Following FPTI IQ Tool analytics requirement
      error_desc: get(tags, 'errorDesc'),
      // Used to log unexpected JS error, to be consistent with default JS error handling tags by pa.js
      error_description: get(tags, 'errorDescription'),
      error_msg: get(tags, 'errorMessage'),
      capabilities: get(tags, 'capabilities'),
      card_type: get(tags, 'card_type'),
      event_name: get(tags, 'eventName'),
      card_form: get(tags, 'cardForm'),
      form_factor: get(tags, 'formFactor'),
      flow_name: get(tags, 'flowName'),
      // Used to determine the selected tab (if any)
      tab: get(tags, 'tab', ''),
      widget_id: get(tags, 'widgetId', ''),
      // partner fpti tags
      clickref: get(tags, 'clickref'),
      pubname: get(tags, 'pubname'),
      pubid: get(tags, 'pubid'),
      flow: get(tags, 'flow'),
      entrypoint: get(tags, 'entrypoint'),
    }

    const dropUndefinedFptiTags = () => {
      // drop keys with undefined values so undefined values will not overwrite possibly existing keys
      Object.keys(opts).forEach((key) => opts[key] === undefined && delete opts[key])
    }

    dropUndefinedFptiTags()

    const data = {
      ...initFPTIData,
      ...FPTIData,
      ...opts,
    }

    // recordImpression sets e:'im'. logActivity sets e: 'ac'
    if (logActivityInstead) {
      instance.logActivity(data)
      return
    }

    instance.recordImpression({ data })
  }
}

function recordClick(stub, link, tags = {}) {
  const instance = getInstance()
  if (instance) {
    const opts = {
      ...allTagsNulled, // if adding a new tag, add it in this object too
      page: FPTIData.page.replace('@stub', stub),
      link,
      context_id: getContextId(tags),
      fi_id: get(tags, 'fiId'),
      fi_type: get(tags, 'fiType'),
      contingency: get(tags, 'contingency'),
      flfr: get(tags, 'fptiEntryPoint'),
      entry_point: get(tags, 'fptiEntryPoint'),
      chosen_fi_id: get(tags, 'chosenFiId'),
      issuer_auth: get(tags, 'isAuthRequired'),
      user_guid: get(tags, 'user_guid'),
      session_guid: get(tags, 'session_guid'),
      card_type: get(tags, 'card_type'),
      event_name: get(tags, 'eventName'),
      opt_sel: get(tags, 'optionSelected'),
      form_factor: get(tags, 'formFactor'),
      tab: get(tags, 'tab'),
      widget_id: get(tags, 'widgetId'),
    }
    instance.recordClick({
      data: Object.assign({}, FPTIData, opts),
    })
  }
}

function startCPLTracking(stub) {
  const analytics = getAnalytics()
  if (analytics) {
    const opts = { page: FPTIData.page.replace('@stub', stub) }
    analytics.startCPLTracking({ data: Object.assign({}, FPTIData, opts) })
  }
}

function endCPLTracking() {
  const analytics = getAnalytics()
  if (analytics) {
    analytics.endCPLTracking()
  }
}

/**
 * Generates a uuid of default length = 32 to differentiate instances of flows.
 * Every time a new flow is loaded, it gets a new context_id
 * If the same flow is loaded, unloaded, and then loaded again, its context_id will be different.
 *
 * Currently, all the routes inside App.js delineate separate flows.
 */
const numbers = [...Array(10)].map((_, i) => String.fromCharCode(i + 48))
const alphaNumerics = [...Array(26)].map((_, i) => String.fromCharCode(i + 97)).concat(numbers)

function generateUUID(len = 32, arr = alphaNumerics) {
  let ans = ''
  for (let i = len; i > 0; i--) {
    ans += arr[Math.floor(Math.random() * arr.length)]
  }
  return ans
}

function generateFptiHeader(flowInstanceId) {
  return { contextId: flowInstanceId }
}

function resetFptiTags() {
  window.fpti = Object.assign({}, initFPTIData)
}

function recordErrorImpression(pageStub, tags = {}, logActivityInstead = false) {
  const {
    errorPage = '',
    errorFields = [],
    errorCode = '',
    eventName = '',
    errorType = '',
    errorDesc = '',
    errorMessage = '',
  } = tags
  recordImpression(
    pageStub,
    {
      errorPage,
      errorField: errorFields.join(':'),
      errorCode,
      eventName,
      errorType,
      errorDesc,
      errorMessage,
    },
    logActivityInstead,
  )
}

function recordClientClick(data) {
  const instance = getInstance()
  if (instance) {
    try {
       const mergedData = {
        ...initFPTIData,
        ...FPTIData,
        ...data
      }
      data = renameKeys(mergedData, { eventName: 'event_name' })
      instance.recordClick({ data })
    } catch (e) {
      instance.recordEvent(recordFptiErrorTags.eventName, {
        error_message: e.message,
        error_description: e.stack,
      })
    }
  }
}

function recordClientEvent(eventName, data) {
  const instance = getInstance()
  if (instance) {
    try {
      const mergedData = {
        ...initFPTIData,
        ...FPTIData,
        ...data
      }
      instance.recordEvent(eventName, mergedData)
    } catch (e) {
      instance.recordEvent(recordFptiErrorTags.eventName, {
        error_message: e.message,
        error_description: e.stack,
      })
    }
  }
}

/**
 * Record API request event
 * @param {string} apiName - Name of the API being called
 * @param {object} tags - Additional FPTI tags
 */
function recordApiRequest(apiName, tags = {}) {
  recordClientEvent(EnrollCardEvents.API_DATA_REQUESTED, {
    api_call: apiName,
    response_status: 'requested',
    ...tags
  })
}

/**
 * Record API response event
 * @param {string} apiName - Name of the API that responded
 * @param {object} tags - Additional FPTI tags
 * @param {boolean} success - Whether the API call was successful
 * @param {object} responseData - Additional response metadata
 */
function recordApiResponse(apiName, tags = {}, success = true, responseData = {}) {
  recordClientEvent(EnrollCardEvents.API_RESPONSE_RECEIVED, {
    api_call: apiName,
    response_status: success ? 'success' : 'error',
    ...tags,
    ...responseData
  })
}

export {
  FPTIData,
  initFPTI,
  // logActivity & logActivityWithLastImpression are deprecated, please use recordClientEvent
  logActivity,
  logActivityWithLastImpression,
  recordClientEvent,
  recordImpression,
  // recordClick is deprecated, please use recordClientClick
  recordClick,
  recordClientClick,
  recordErrorImpression,
  startCPLTracking,
  endCPLTracking,
  generateUUID,
  generateFptiHeader,
  resetFptiTags,
  recordApiRequest,
  recordApiResponse,
}
